import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Icon,
  Skeleton,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import React from 'react';
import { IconType } from 'react-icons';
import { FiExternalLink, FiDownload } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

interface ReportCardProps {
  title: string;
  description: string;
  icon: IconType;
  iconColor?: string;
  statValue?: string | number;
  statLabel?: string;
  statHelpText?: string;
  viewLink?: string;
  onExport?: () => void;
  isLoading?: boolean;
  isExporting?: boolean;
}

const ReportCard: React.FC<ReportCardProps> = ({
  title,
  description,
  icon,
  iconColor = 'blue.500',
  statValue,
  statLabel,
  statHelpText,
  viewLink,
  onExport,
  isLoading = false,
  isExporting = false,
}) => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconBgColor = useColorModeValue('gray.50', 'gray.700');

  const handleViewClick = () => {
    if (viewLink) {
      navigate(viewLink);
    }
  };

  return (
    <Card
      bg={bgColor}
      borderColor={borderColor}
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{
        transform: 'translateY(-4px)',
        shadow: 'lg',
      }}
    >
      <CardHeader pb={2}>
        <Flex justify="space-between" align="center">
          <HStack spacing={3}>
            <Flex
              w="48px"
              h="48px"
              bg={iconBgColor}
              borderRadius="md"
              align="center"
              justify="center"
            >
              <Icon as={icon} boxSize={6} color={iconColor} />
            </Flex>
            <Box>
              <Heading size="md" mb={1}>
                {title}
              </Heading>
              <Text fontSize="sm" color="gray.500">
                {description}
              </Text>
            </Box>
          </HStack>
        </Flex>
      </CardHeader>

      <CardBody py={4}>
        {isLoading ? (
          <Skeleton height="80px" borderRadius="md" />
        ) : (
          statValue !== undefined && (
            <Stat>
              <StatLabel fontSize="sm" color="gray.500">
                {statLabel}
              </StatLabel>
              <StatNumber fontSize="3xl" fontWeight="bold">
                {statValue}
              </StatNumber>
              {statHelpText && (
                <StatHelpText fontSize="sm">{statHelpText}</StatHelpText>
              )}
            </Stat>
          )
        )}
      </CardBody>

      <CardFooter pt={0}>
        <VStack w="full" spacing={2}>
          {viewLink && (
            <Button
              w="full"
              colorScheme="blue"
              rightIcon={<FiExternalLink />}
              onClick={handleViewClick}
              isDisabled={isLoading}
            >
              Ver Detalhes
            </Button>
          )}
          {onExport && (
            <Button
              w="full"
              variant="outline"
              colorScheme="green"
              rightIcon={<FiDownload />}
              onClick={onExport}
              isLoading={isExporting}
              isDisabled={isLoading}
            >
              Exportar Dados
            </Button>
          )}
        </VStack>
      </CardFooter>
    </Card>
  );
};

export default ReportCard;
